/**
 * Days as a list's filter holds them. A day is chosen as a day
 * (`YYYY-MM-DD`, what a date field writes) and asked of a service as moments
 * of the market's clock: from the start of the first day to the start of the
 * day after the last, since the services read "to" as "before this moment".
 */
import { instantIn, localIn, MARKET_TIME_ZONE } from "@/shared/lib/format";

/** A day as a date field writes it; anything else is not a day. */
export function dayOf(value: unknown): string | undefined {
  if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return undefined;
  const [year, month, day] = value.split("-").map(Number) as [number, number, number];
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.getUTCMonth() === month - 1 && date.getUTCDate() === day ? value : undefined;
}

/** A day so many days after another (before it, for a number below zero). */
export function dayPlus(day: string, days: number): string {
  const [year, month, date] = day.split("-").map(Number) as [number, number, number];
  return new Date(Date.UTC(year, month - 1, date + days)).toISOString().slice(0, 10);
}

/** Today by the market's clock, whatever the computer's own says. */
export function marketToday(now: Date = new Date(), timeZone = MARKET_TIME_ZONE): string {
  return localIn(now, timeZone).slice(0, 10);
}

export interface Moments {
  /** From this moment on. */
  from?: string;
  /** Before this moment. */
  to?: string;
}

/** A span of the market's days as the moments it begins and ends at; an open side is left out. */
export function momentsOfDays(
  from: string | undefined,
  to: string | undefined,
  timeZone = MARKET_TIME_ZONE,
): Moments {
  const moments: Moments = {};
  const begins = from === undefined ? null : instantIn(`${from}T00:00`, timeZone);
  if (begins !== null) moments.from = begins;
  const ends = to === undefined ? null : instantIn(`${dayPlus(to, 1)}T00:00`, timeZone);
  if (ends !== null) moments.to = ends;
  return moments;
}
